import React, { useEffect, useState, useRef, useImperativeHandle, useMemo } from 'react';
import { TextField, Callout, FontWeights, Text, DirectionalHint, TooltipHost, FontIcon, makeStyles, useTheme, Stack } from '@fluentui/react';
import { FormatValue, GetNumberSep, MathToFixed, getEmptyGuid } from './Utils';
import { constants } from '../Styles/constants';
import { PeriodDay } from '../types/PeriodDay';
import EDays from '../types/EDays';
import { useCellCtx } from './Contexts/CellContext';
import { EndPointSolidIcon } from '@fluentui/react-icons-mdl2';
import { roundToNearestValueStep } from '../Mobile/mobileUtils';
import { useId } from '@fluentui/react-hooks';
import { TimeEntry, UserRegistrationPeriod } from '../api/generated/data-contracts';
import { useGridContext } from './Contexts/GridContext/context';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { useConfigValues } from '../hooks/queries/useConfigValues';
import { TimeGridMode } from '../types/TimeGridMode';

interface CellProps {
    time: UserRegistrationPeriod;
    timeentry: TimeEntry;
    day: PeriodDay;
    updated: Function;
    readonlyreason: string;
    timeLowerLimitConfig: string;
    cellDataNeedsSave: Function;
}

const useHandleReadOnly = ({ readonlyreason, timeentry }: Pick<CellProps, 'readonlyreason' | 'timeentry'>) => {
    const { t } = useTranslation('timesheet');
    const {
        data: {
            configuration: { lockProcessedTimeEntries },
        },
    } = useConfigValues();
    return useMemo(() => {
        if (readonlyreason) {
            return readonlyreason;
        }
        if (timeentry?.approval?.externalId && lockProcessedTimeEntries) {
            return t('readOnlyMessages.externallyLocked');
        }
        return '';
    }, [readonlyreason, timeentry, t, lockProcessedTimeEntries]);
};

const Cell = React.forwardRef<any, CellProps>((props, ref) => {
    const { t } = useTranslation('timesheet');
    const theme = useTheme();
    const styles = getStyles();
    const textFieldRef = useRef<any>();
    const targetId = useId();
    const errorTargetId = useId();

    const [celltext, setCelltext] = useState('');
    const [cellnumber, setCellnumber] = useState<number>(0);
    const [cellcomment, setComment] = useState<string>('');
    const [cellFlagged, setCellFlagged] = useState<boolean>(props.timeentry?.flagged || false);

    const [cellnumberlastsaved, setCellnumberlastsaved] = useState<number>(0);
    const [commentlastsaved, setCommentlastsaved] = useState<string>('');

    const [cellIsError, setCellIsError] = useState<string>('');

    const [workday, setWorkday] = useState(true);
    const [showdetailsicon, setShowdetailsicon] = useState<boolean>(false);
    const [showdetailsflyout, setShowdetailsflyout] = useState<boolean>(false);
    const [showFlagIcon, setShowFlagIcon] = useState<boolean>(props.timeentry?.flagged || false);
    const [textinfocus, setTextinfocus] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);

    const [anyChanges, setAnyChanges] = useState<boolean>(false);
    const [dataSaved, setdatasaved] = useState<boolean>(false);

    const cellCtx = useCellCtx();
    const { setCellsWithError, cellsWithError } = useGridContext();

    useEffect(() => {
        //For total update?
        const hours = props.timeentry?.hours || 0;
        setCelltext(FormatValue(hours));
        setCellnumber(hours);
        setCellnumberlastsaved(hours);
    }, [props.timeentry?.hours]);

    useEffect(() => {
        const hours = props.timeentry?.hours || 0;
        const comment = props.timeentry?.comment || '';
        setCelltext(FormatValue(hours));
        setCellnumber(hours);
        setCellnumberlastsaved(hours);
        setComment(comment);
        setCommentlastsaved(comment);
        const dayName = props.day.originalName.toLowerCase();
        if (dayName === EDays.Saturday.toLowerCase() || dayName === EDays.Sunday.toLowerCase()) {
            setWorkday(false);
        }

        setLoaded(true);
    }, [props.timeentry]);

    useEffect(() => {
        const hours = props.timeentry?.hours || 0;
        setCelltext(FormatValue(hours));
    }, [props.timeentry, props.day]);

    const {
        initialData: {
            configuration: { timeEntryRoundingInterval, allowCellComment, allowCellFlag },
        },
    } = useConfigValuesState();

    useEffect(() => {
        if (showdetailsflyout === false && textinfocus === false && loaded === true) {
            console.debug('Cell is error changed: ' + cellIsError);
            const roundedCellNumber = roundToNearestValueStep(cellnumber, timeEntryRoundingInterval);
            if (cellIsError == '' && (roundedCellNumber !== cellnumberlastsaved || cellcomment !== commentlastsaved)) {
                setdatasaved(true);
                saveData(roundedCellNumber, cellcomment, cellnumberlastsaved, cellFlagged);
                setAnyChanges(false);
            } else {
                setCelltext(FormatValue(cellnumberlastsaved));
                setCellnumber(cellnumberlastsaved);
            }
            props.cellDataNeedsSave(false);
        }
    }, [showdetailsflyout, textinfocus]);

    useEffect(() => {
        if (textinfocus && textFieldRef.current) {
            textFieldRef.current.select();
        }
    }, [textinfocus]);

    useEffect(() => {
        if (cellIsError == '') {
            // Check if ctx contains cell, if so, remove it
            var newArray = cellsWithError.filter(c => c != targetId);
            setCellsWithError(newArray);
        } else {
            //Check if ctx contains cell, if not, add it
            if (cellsWithError.filter(c => c == targetId).length < 1) {
                var newArray = cellsWithError;
                newArray.push(targetId);
                setCellsWithError(newArray);
            }
        }
        //console.debug('niv test ctx ---', uiCtx.cellsWithError);
    }, [cellIsError]);

    const onCellUpdated = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        setAnyChanges(true);

        isValid(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
        setCelltext(GetNumberSep() === ',' ? text.replace(/\./g, ',') : text.replace(/\,/g, '.'));
        setCellnumber(MathToFixed(parseNumber(text.replace(/\,/g, '.')), 2));
    };

    const onCommentUpdated = (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, text: string): void => {
        setComment(text);
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setTimeout(() => {
            setTextinfocus(false);
            setShowdetailsicon(false);
            setShowFlagIcon(cellFlagged);
            setCellIsError('');
        }, 500);
    };

    const isValid = (value: string): void => {
        const maxHoursperDay = 24;
        const newVal = value.replace(/,/g, '.');
        const maxHoursMessage = t('cell.error.maxhours', { value: maxHoursperDay });
        const negativeMessage = t('cell.error.negative');
        const invalidMessage = t('cell.error.invalid');
        if (isValidNumber(newVal)) {
            const newValNum = parseFloat(newVal);
            if (newValNum > maxHoursperDay) {
                setCellIsError(maxHoursMessage);
                return;
            }
            if (newValNum < 0) {
                setCellIsError(negativeMessage);
                return;
            }
            setCellIsError('');
        } else {
            setCellIsError(invalidMessage);
        }
    };

    const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        props.cellDataNeedsSave(true);
        setShowdetailsicon(true);
        setShowFlagIcon(true);
        setTextinfocus(true);

        console.log('PROPS', props);
    };

    const onIconClick = (ev: React.MouseEvent<HTMLElement, MouseEvent>): void => {
        setShowdetailsflyout(true);
        setShowdetailsicon(true);
        setShowFlagIcon(true);
    };

    const onFlagIconClick = () => {
        const newFlagValue = !cellFlagged;
        saveData(cellnumber, cellcomment, cellnumberlastsaved, newFlagValue);
    };

    const flyoutDismissed = (ev: any): void => {
        setShowdetailsflyout(false);
    };

    const focusCellIfExists = (cellKey: string) => {
        // If cellKey is defined, it was found in the Map containing all cells
        // and it can be focused
        if (cellKey) {
            const destinationCell = cellCtx.inputNodes.get(cellKey);
            destinationCell.setFocus();
        }
    };

    const getKeyForTargetDay = (currentCellKey: string, daysFromCurrent: number) => {
        const keys = [...cellCtx.inputNodes.keys()];
        const indexOfCurrent = keys.indexOf(currentCellKey);

        return keys[indexOfCurrent + daysFromCurrent];
    };

    // If standing in Friday cell, move to next row
    const focusNextCell = (currentRowId: any, currentDay: any) => {
        const nextCellKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, 1);
        focusCellIfExists(nextCellKey);
    };

    const focusPreviousCell = (currentRowId: any, currentDay: any) => {
        const previousKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, -1);
        focusCellIfExists(previousKey);
    };

    const focusBelowCell = (currentRowId: any, currentDay: any) => {
        const keys = [...cellCtx.inputNodes.keys()];
        let highest = 0;

        keys.some(function (key: string) {
            // get day index of the current key - what is after the last underscore
            const dayIndex: number = +key.substring(key.lastIndexOf('_') + 1);

            if (dayIndex >= highest) {
                highest = dayIndex;
            } else {
                // we found the highest dayIndex - return true to break .some() loop
                return true;
            }
        });

        const oneWeekAheadKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, highest + 1);
        focusCellIfExists(oneWeekAheadKey);
    };

    const focusAboveCell = (currentRowId: any, currentDay: any) => {
        const keys = [...cellCtx.inputNodes.keys()];
        const daysInPeriod = getDaysInWeek(keys);
        const oneWeekBeforeKey = getKeyForTargetDay(`${currentRowId}_${currentDay}`, -(daysInPeriod + 1));
        focusCellIfExists(oneWeekBeforeKey);
    };

    const onKeyDown = (event: any): void => {
        const workTypeId = props.time.workType == null || props.time.workType == undefined ? getEmptyGuid() : props.time.workType.id;
        const rowId = `${props.time.taskId}_${workTypeId}`;

        switch (event.keyCode) {
            case 13:
                // Enter key
                focusBelowCell(rowId, props.day.id);
                break;

            case 37:
                // Left arrow
                focusPreviousCell(rowId, props.day.id);
                break;

            case 38:
                // Up arrow
                focusAboveCell(rowId, props.day.id);
                break;

            case 39:
                // Right arrow
                focusNextCell(rowId, props.day.id);
                break;

            case 40:
                // Down arrow
                focusBelowCell(rowId, props.day.id);
                break;

            default:
                return;
        }

        if (cellnumber !== cellnumberlastsaved || cellcomment !== commentlastsaved) {
            if (cellIsError == '') {
                setdatasaved(true);
                saveData(cellnumber, cellcomment, cellnumberlastsaved, cellFlagged);
                setAnyChanges(false);
            }
        }
    };

    const saveData = (hours: number, comment: string, previousHours: number, isFlagged: boolean): void => {
        //clearTimeout(savetimer);
        const hoursToBeSaved = hours;
        const commentToBeSaved = comment;
        const flagged = previousHours >= 0 && hoursToBeSaved === 0 ? false : isFlagged;

        console.debug('saving timer startet...');
        //savetimer = setTimeout(() => {
        if (!showdetailsflyout) {
            props.updated(props.time, props.day, props.timeentry, hoursToBeSaved, commentToBeSaved, flagged, TimeGridMode.MyTime, previousHours, () => {
                setTimeout(() => {
                    setdatasaved(false);
                    setShowFlagIcon(flagged);
                    setCellFlagged(flagged);

                    if (comment === '' && showdetailsicon) {
                        setShowdetailsicon(false);
                    }
                }, 200);
            });
            setCellnumberlastsaved(hoursToBeSaved);
            setCommentlastsaved(commentToBeSaved);
            console.debug('saving (' + hoursToBeSaved + ', ' + commentToBeSaved + ')...');
        }
    };

    useImperativeHandle(ref, () => ({
        setFocus: () => textFieldRef.current && textFieldRef.current.focus(),
    }));

    const readonlyReason = useHandleReadOnly({ readonlyreason: props.readonlyreason, timeentry: props.timeentry });
    const readonly = Boolean(readonlyReason);

    return (
        <div className={styles.root}>
            {allowCellComment && (showdetailsicon || showdetailsflyout || cellcomment !== '') ? (
                <FontIcon
                    id={targetId}
                    iconName="Comment"
                    className={'dayDetailsIcon' + (readonly ? ' dayDetailsIconDisabled' : '')}
                    onClick={onIconClick}
                    data-testid={`comment-button`}
                />
            ) : null}
            <TooltipHost content={readonly ? readonlyReason : ''} closeDelay={250}>
                <TextField
                    id={errorTargetId}
                    componentRef={textFieldRef}
                    className="cellInput"
                    value={celltext}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onChange={onCellUpdated}
                    onKeyDown={onKeyDown}
                    disabled={readonly}
                    autoComplete="off"
                    style={{ textAlign: 'center' }}
                    styles={{
                        root: {},
                        fieldGroup: {
                            borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,0.75)' : 'rgba(0,0,0,0)'}`,
                            backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                            borderWidth: '2px',
                            '&:hover': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)' : 'rgba(0,0,0,0)'}`,
                                backgroundColor: `${dataSaved ? 'rgba(16, 124, 16, 0.6)!important' : 'rgba(0,0,0,0)'}`,
                            },
                            '&::after': {
                                borderColor: `${cellIsError !== '' ? 'rgba(155,42,49,1)' : 'rgba(96,97,170,1)'}`,
                            },
                        },
                        field: {
                            textAlign: 'right',
                            paddingRight: theme.spacing.s1,
                            paddingLeft: 7,
                        },
                    }}
                />
                {allowCellFlag && showFlagIcon ? (
                    <EndPointSolidIcon
                        id={targetId}
                        style={{ color: cellFlagged ? theme.palette.red : theme.palette.themeLight, cursor: readonly ? 'no-drop' : 'pointer' }}
                        onClick={() => {
                            if (!readonly) {
                                onFlagIconClick();
                            }
                        }}
                        className={'dayflagsIcon' + (readonly ? ' dayflagsIconDisabled' : '')}
                    />
                ) : null}
            </TooltipHost>

            {cellIsError !== '' && (
                <Callout
                    className={styles.calloutError}
                    ariaLabelledBy="{labelId}"
                    ariaDescribedBy="{descriptionId}"
                    role="alertdialog"
                    gapSpace={0}
                    target={`#${errorTargetId}`}
                    directionalHint={DirectionalHint.topCenter}
                    setInitialFocus
                    dismissOnTargetClick={true}
                >
                    <Text>{cellIsError}</Text>
                </Callout>
            )}

            {showdetailsflyout && (
                <Callout
                    className={styles.callout}
                    ariaLabelledBy="{labelId}"
                    ariaDescribedBy="{descriptionId}"
                    role="commentdialog"
                    gapSpace={0}
                    target={`#${targetId}`}
                    directionalHint={DirectionalHint.bottomCenter}
                    onDismiss={flyoutDismissed}
                    setInitialFocus
                    dismissOnTargetClick={true}
                >
                    <Stack>
                        <div className={styles.header}>
                            <Text className={styles.title}>{t('comment.title')}</Text>
                        </div>
                        <div className={styles.inner}>
                            <TextField value={cellcomment} onChange={onCommentUpdated} multiline autoAdjustHeight disabled={readonly} />
                        </div>
                    </Stack>
                </Callout>
            )}
        </div>
    );
});

export default Cell;

const isValidNumber = (num: string) => {
    if (num === '') {
        return true;
    }
    if (isNaN(parseFloat(num))) {
        return false;
    }
    return true;
};

const parseNumber = (num: string) => {
    if (num === '') {
        return 0;
    }
    if (isNaN(parseFloat(num))) {
        return 0;
    }
    return parseFloat(num);
};

const getStyles = makeStyles(theme => ({
    root: [
        'cellInner',
        {
            position: 'relative',
            height: '100%',
            width: '100%',
            maxWidth: constants.cellInputMaxWidth,
            margin: '0 auto',
            display: 'flex',
        },
    ],
    buttonArea: {
        verticalAlign: 'top',
        display: 'inline-block',
        textAlign: 'center',
        margin: '0 100px',
        minWidth: 130,
        height: 32,
    },
    callout: {
        maxWidth: 800,
        minWidth: 300,
    },
    calloutError: {
        width: 100,
        textAlign: 'center',
    },
    header: {
        padding: '18px 24px 12px',
        color: theme.semanticColors.bodyText,
    },
    title: [
        theme.fonts.mediumPlus,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    inner: {
        height: '100%',
        padding: '0 24px 20px',
    },
    actions: {
        position: 'relative',
        marginTop: 20,
        width: '100%',
        whiteSpace: 'nowrap',
    },
    subtext: [
        theme.fonts.small,
        {
            margin: 0,
            fontWeight: FontWeights.semilight,
        },
    ],
    link: [
        theme.fonts.medium,
        {
            color: theme.palette.neutralPrimary,
        },
    ],
}));

function getDaysInWeek(keys: any[]) {
    const indexes: number[] = [];

    keys.forEach((key: string) => {
        // convert dayIndex to number
        const dayIndex: number = +key.substring(key.lastIndexOf('_') + 1);
        indexes.push(dayIndex);
    });

    // this indicates how many days we have in the period
    const daysInWeek = Math.max(...indexes);
    return daysInWeek;
}
